/**
 * @fileoverview `<author>/<slug>` in, a page ADDRESS out. Always an address.
 *
 * The readable URL is a CONVENIENCE THAT RESOLVES TO THE CANONICAL ONE, never a
 * separate identity, and the direction is the entire point: an `naddr` is
 * self-contained and works for anyone's page on any relay with no lookup
 * against us, so inverting the order would make our own infrastructure a single
 * point of failure for other people's links.
 *
 * THE AUTHOR SEGMENT TAKES AN `npub` OR `nprofile` AS WELL AS A NAME, and that
 * is not a convenience -- it is what makes the readable form honest. The design
 * describes this half as resolving "through our identity service", which reads
 * as though the whole readable form depends on a service we run. It does not
 * have to. The property the readable form actually exists for is SURVIVING A
 * KIND CHANGE, and that comes from resolving the slug across both page kinds
 * further down this file, not from the name lookup. So `/<npub>/<slug>` delivers
 * the whole of it with no name service in the path at all, which matters today
 * because identity serves no names yet (measured 2026-09-15: the endpoint is
 * live and answers `{"names":{}}`).
 *
 * WHY BOTH KINDS ARE STILL ASKED, THOUGH THE REASON CHANGED ON 2026-09-16.
 * Until that ruling a public markdown page was a 30023 and only a blob-backed
 * page was ours, so flipping a page to private changed its kind and therefore
 * its `naddr`. The readable link was the form we told people to share precisely
 * BECAUSE looking in both kinds survived that. Every page is a 33301 now and
 * the flip changes no address, so that reason is gone and the behaviour is not,
 * because two others took its place. Pages published under the old rule ARE
 * 30023 and the links people hold to them have to keep working. And an author's
 * long-form articles, written in clients that have never heard of us, share
 * this namespace whether we like it or not -- which is the collision
 * `findSlugHolders` refuses at publish time, and now the only one left.
 *
 * WHEN BOTH KINDS ANSWER ANYWAY, THE NEWEST WINS. The publish check is best
 * effort by construction -- there is no server to be authoritative and a silent
 * relay is indistinguishable from a free name -- so a duplicate can exist,
 * having been made by another client or slipped past a relay that did not
 * answer. Newest-by-`created_at` is not a tie-break of convenience: it is
 * exactly right for the case that produces duplicates on purpose, the
 * public-to-private flip, where the new kind is by definition the later event.
 *
 * THIS FUNCTION HAS NO FAILURE RETURN, AND THAT IS DELIBERATE. A name nobody
 * has registered, an `naddr` pasted into the author slot and a slug the author
 * never used all produce a well-formed address that names nothing. The caller
 * therefore has no branch to write and no second "not available" screen to
 * word: the address goes to `loadPage` like any other, which collapses every
 * way it can end badly into one outcome behind a timing floor. Short-circuiting
 * here would put a faster path to the same screen and undo that -- a private
 * page, whose `d` tag stays plaintext, resolves and then fails slowly, so a
 * fast failure would mean "nothing here" and a slow one "something you may not
 * read".
 */

import { PAGE_INDEX_KIND } from './kinds';
import { findSlugHolders } from './slugAvailability';

import { decodeIdentifier } from '@/services/nostr/identifiers';
import { resolveName, type ResolvedName } from '@/services/nostr/nip05';
import { encodeAddress } from '@/services/nostr/pageLinks';
import { relaysFor } from '@/services/nostr/relays';

/**
 * The author of an address that names nothing.
 *
 * A structurally valid pubkey that no key can produce, so the query it causes
 * is real and finds nothing, and the reader's failure takes the same shape and
 * the same route as every other failure. See the header on why there is no
 * early return to put here instead.
 */
const NOBODY = '0'.repeat(64);

/**
 * Turn the two readable segments into the page's canonical `naddr`.
 *
 * The default kind when nothing is found is ours, because since 2026-09-16 that
 * is what a page is. Nothing rests on the choice -- the address names no event
 * either way and the reader lands on the same screen -- but a dead end that
 * named the long-form kind would be pointing at the one kind we no longer
 * publish, and the next person to read it would take that for a decision.
 */
export async function resolveReadableAddress(author: string, slug: string): Promise<string> {
  const who = await identifyAuthor(author);
  if (!who) {
    return encodeAddress({ kind: PAGE_INDEX_KIND, pubkey: NOBODY, identifier: slug });
  }

  const holders = await findSlugHolders(who.pubkey, slug, relaysFor(who.relays));
  const newest = holders.reduce<(typeof holders)[number] | null>(
    (best, h) => (!best || h.createdAt > best.createdAt ? h : best),
    null
  );

  return encodeAddress({
    kind: newest?.kind ?? PAGE_INDEX_KIND,
    pubkey: who.pubkey,
    identifier: slug,
    relays: who.relays,
  });
}

/**
 * Who the first segment names, or null.
 *
 * An `npub` or `nprofile` is decoded locally and costs no request. Anything
 * else that is a NIP-19 identifier -- an `naddr`, a `note` -- is a reference to
 * something that is not a person, and is refused rather than being passed to
 * the name service, which would look up a bech32 string as though it were a
 * name. Only a string that is not NIP-19 at all is treated as a name.
 */
async function identifyAuthor(author: string): Promise<ResolvedName | null> {
  let decoded;
  try {
    decoded = decodeIdentifier(author);
  } catch {
    // An nsec in a URL. Not decoded, not named, not echoed, and not looked up.
    return null;
  }

  if (decoded) {
    return decoded.type === 'profile'
      ? { pubkey: decoded.pubkey, relays: decoded.relays }
      : null;
  }

  return resolveName(author);
}
